import { useCallback } from "react";
import type { UseInstantTransformEvent } from "@b2bportal/core-tracking";
import { Funnel } from "@b2bportal/core-types";
import type {
  ActivityEntryRawEvent,
  ActivityEntryEvent,
  CommonAuthRawEvent,
  CommonAuthEvent,
  ViewedFlightCheckoutRawEvent,
  ViewedFlightCheckoutEvent,
  ViewedCarCheckoutRawEvent,
  ViewedCarCheckoutEvent,
  ViewedHotelCheckoutRawEvent,
  ViewedHotelCheckoutEvent,
  ViewedPackageCheckoutRawEvent,
  ViewedPackageCheckoutEvent,
  ViewedActivitiesCheckoutRawEvent,
  ViewedActivitiesCheckoutEvent,
} from "./events";
import {
  RbcActivityEntryEventName,
  RbcAuthEventName,
  RbcCheckoutEventName,
} from "./events";
import type {
  ViewPaymentRawEvent,
  ViewPaymentEvent,
  ChoosePaymentRawEvent,
  ChoosePaymentEvent,
} from "./events/raw/PaymentRawEvent";
import { PaymentEventName } from "./events/raw/PaymentRawEvent";
import type {
  ChooseTravelerRawEvent,
  ChooseTravelerEvent,
} from "./events/raw/TravelerRawEvent";
import { TravelerEventName } from "./events/raw/TravelerRawEvent";
import { useTrackFunnelEntry } from "./hooks";
import { usePackageEventAttribution } from "./upSell";

/**
 * Transformer for common_auth event
 * Passes through accountName and tier from session info
 */
export const useCommonAuthTransformer: UseInstantTransformEvent<
  CommonAuthRawEvent,
  CommonAuthEvent
> = () => {
  return useCallback(
    (event) => ({
      name: RbcAuthEventName.common_auth,
      properties: {
        accountName: event.properties.accountName,
        tier: event.properties.tier,
        user_province: event.properties.user_province,
      },
    }),
    []
  );
};

/**
 * Transformer for viewed_flight_checkout event
 */
export const useViewedFlightCheckoutTransformer: UseInstantTransformEvent<
  ViewedFlightCheckoutRawEvent,
  ViewedFlightCheckoutEvent
> = () => {
  return useCallback(
    (event) => ({
      name: RbcCheckoutEventName.viewed_flight_checkout,
      properties: {
        rbc_price_tab: event.properties?.rbc_price_tab,
      },
    }),
    []
  );
};

/**
 * Transformer for viewed_car_checkout event
 */
export const useViewedCarCheckoutTransformer: UseInstantTransformEvent<
  ViewedCarCheckoutRawEvent,
  ViewedCarCheckoutEvent
> = () => {
  return useCallback(
    () => ({
      name: RbcCheckoutEventName.viewed_car_checkout,
      properties: {},
    }),
    []
  );
};

/**
 * Transformer for viewed_hotel_checkout event
 */
export const useViewedHotelCheckoutTransformer: UseInstantTransformEvent<
  ViewedHotelCheckoutRawEvent,
  ViewedHotelCheckoutEvent
> = () => {
  return useCallback(
    () => ({
      name: RbcCheckoutEventName.viewed_hotel_checkout,
      properties: {},
    }),
    []
  );
};

/**
 * Transformer for viewed_package_checkout event
 */
export const useViewedPackageCheckoutTransformer: UseInstantTransformEvent<
  ViewedPackageCheckoutRawEvent,
  ViewedPackageCheckoutEvent
> = () => {
  const attribution = usePackageEventAttribution();

  return useCallback(
    () => ({
      name: RbcCheckoutEventName.viewed_package_checkout,
      properties: { ...attribution },
    }),
    [attribution]
  );
};

/**
 * Transformer for viewed_activities_checkout event
 */
export const useViewedActivitiesCheckoutTransformer: UseInstantTransformEvent<
  ViewedActivitiesCheckoutRawEvent,
  ViewedActivitiesCheckoutEvent
> = () => {
  return useCallback(
    () => ({
      name: RbcCheckoutEventName.viewed_activities_checkout,
      properties: {},
    }),
    []
  );
};

/**
 * Transformer for view_payment event
 * Used across all products (flights, hotels, cars, packages, activities)
 */
export const useViewPaymentTransformer: UseInstantTransformEvent<
  ViewPaymentRawEvent,
  ViewPaymentEvent
> = () => {
  return useCallback(
    (event) => ({
      name: PaymentEventName.view_payment,
      properties: {
        product_type: event.properties.product_type,
        over_max_ticket_price: event.properties.over_max_ticket_price,
        overage_amount: event.properties.overage_amount,
        additional_points_purchase: event.properties.additional_points_purchase,
        additional_points_purchase_total:
          event.properties.additional_points_purchase_total,
        additional_points_needed: event.properties.additional_points_needed,
      },
    }),
    []
  );
};

/**
 * Transformer for choose_payment event
 */
export const useChoosePaymentTransformer: UseInstantTransformEvent<
  ChoosePaymentRawEvent,
  ChoosePaymentEvent
> = () => {
  return useCallback(
    () => ({
      name: PaymentEventName.choose_payment,
      properties: {},
    }),
    []
  );
};

/**
 * Transformer for choose_traveler event
 * Used across all products (flights, hotels, cars, packages, activities)
 */
export const useChooseTravelerTransformer: UseInstantTransformEvent<
  ChooseTravelerRawEvent,
  ChooseTravelerEvent
> = () => {
  return useCallback(
    (event) => ({
      name: TravelerEventName.choose_traveler,
      properties: {
        product_type: event.properties.product_type,
      },
    }),
    []
  );
};

/**
 * RBC-specific transformer for activities_entry event.
 * Fires on every funnel transition (not just once per session).
 *
 * Uses the shared `useTrackFunnelEntry` hook with Funnel.Activities.
 */
export const useActivityEntryTransformer: UseInstantTransformEvent<
  ActivityEntryRawEvent,
  ActivityEntryEvent
> = () => {
  const shouldTrack = useTrackFunnelEntry(Funnel.Activities);

  return useCallback(
    (event) => {
      if (!shouldTrack) {
        return undefined;
      }

      return {
        name: RbcActivityEntryEventName.activities_entry,
        properties: { ...event.properties },
      };
    },
    [shouldTrack]
  );
};
